// ╔══════════════════════════════════════════════════════════════╗
// ║  Eng Dashboard — production-focused (no salaries)             ║
// ║  Stage performance · factory efficiency · today completion    ║
// ╚══════════════════════════════════════════════════════════════╝

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { fmtAR, fmtMoney } from '../lib/format.js';
import { Badge, Btn, Card, KPI, Progress } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import { LineChart } from '../components/Charts.jsx';

export default function EngDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'eng-dashboard'],
    queryFn: () => api.get('/reports/eng-dashboard').then((r) => r.data),
  });
  const { data: cost } = useQuery({
    queryKey: ['reports', 'production-cost'],
    queryFn: () => api.get('/reports/production-cost').then((r) => r.data),
  });

  if (isLoading || !data) {
    return (
      <div className="p-6 lg:p-8">
        <Card className="p-12 text-center text-ink-400">جاري التحميل…</Card>
      </div>
    );
  }

  const { kpis, stages, trend7Days } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            مدير الهندسة
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">
            نظرة عامة على الإنتاج
          </h1>
          <p className="text-sm text-ink-400 mt-1">
            أهلاً، {user?.name?.split(' ').slice(0, 2).join(' ')} — أداء المصنع اليوم وهذا الشهر
          </p>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Btn variant="outline" icon="users" onClick={() => navigate('/production/workers')}>
            إدارة العمال
          </Btn>
          <Btn variant="outline" icon="layers" onClick={() => navigate('/production/stages')}>
            المراحل
          </Btn>
          <Btn variant="primary" icon="plus" onClick={() => navigate('/production/entry')}>
            إدخال إنتاج اليوم
          </Btn>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          label="إجمالي العمال"
          value={fmtAR(kpis.totalWorkers)}
          sub={`${stages.length} مراحل`}
          icon="users"
          accent
          foot={<Badge tone="brand">نشط</Badge>}
        />
        <KPI
          label="إنتاج الشهر"
          value={fmtAR(kpis.totalProduced)}
          sub={`الهدف ${fmtAR(kpis.totalTarget)} قطعة`}
          icon="factory"
          foot={<span className="text-[11px] text-ink-400">{kpis.efficiency}% من الهدف</span>}
        />
        <KPI
          label="كفاءة المصنع"
          value={`${kpis.efficiency}%`}
          sub="فعلي/هدف هذا الشهر"
          icon="trendU"
          foot={
            <span
              className={`text-[11px] font-bold ${
                kpis.efficiency >= 100 ? 'text-emerald-600' : 'text-amber-600'
              }`}
            >
              {kpis.efficiency >= 100 ? 'فوق الهدف ✓' : 'تحت الهدف'}
            </span>
          }
        />
        <KPI
          label="اكتمال إدخال اليوم"
          value={`${kpis.totalWorkers > 0 ? Math.round((kpis.recordedToday / kpis.totalWorkers) * 100) : 0}%`}
          sub={`${fmtAR(kpis.recordedToday)} من ${fmtAR(kpis.totalWorkers)}`}
          icon="check"
          foot={
            kpis.recordedToday === kpis.totalWorkers ? (
              <Badge tone="success" dot>مكتمل</Badge>
            ) : (
              <Btn size="sm" variant="ghost" onClick={() => navigate('/production/entry')}>
                إدخال
              </Btn>
            )
          }
        />
      </div>

      {/* Today summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200 dark:ring-emerald-800 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 grid place-items-center shrink-0">
            <Icon name="trendU" size={18} />
          </div>
          <div>
            <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold uppercase">
              حقّقوا الهدف اليوم
            </div>
            <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300 tabular">
              {fmtAR(kpis.hitsToday)}
            </div>
            <div className="text-[11px] text-ink-400">عامل</div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200 dark:ring-rose-800 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 grid place-items-center shrink-0">
            <Icon name="trendD" size={18} />
          </div>
          <div>
            <div className="text-[11px] text-rose-600 dark:text-rose-400 font-bold uppercase">
              تحت الهدف اليوم
            </div>
            <div className="text-2xl font-black text-rose-700 dark:text-rose-300 tabular">
              {fmtAR(kpis.missesToday)}
            </div>
            <div className="text-[11px] text-ink-400">عامل</div>
          </div>
        </div>
      </div>

      {/* Production trend (last 7 days) */}
      {trend7Days.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold text-base mb-3">إنتاج المصنع — آخر 7 أيام</h3>
          <div className="text-ink-700 dark:text-ink-200">
            <LineChart
              data={trend7Days.map((d) => ({
                label: d.date.slice(8),
                value: d.produced,
              }))}
              color="#cc9c46"
              height={160}
            />
          </div>
        </Card>
      )}

      {/* Stage performance + cost */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base">الأداء عبر خط الإنتاج</h3>
          <Btn
            size="sm"
            variant="ghost"
            iconEnd="arrowL"
            onClick={() => navigate('/production/stages')}
          >
            إعدادات المراحل
          </Btn>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {stages.map((s) => {
            // Find matching stage cost (if loaded)
            const stageCost = cost?.byStage?.find((c) => c.stageId === s.stageId);
            return (
              <button
                key={s.stageId}
                onClick={() => navigate(`/production/workers?stage=${s.stageId}`)}
                className="text-start rounded-2xl p-4 ring-1 ring-ink-100 dark:ring-ink-800 bg-gradient-to-bl from-white to-ink-50/40 dark:from-ink-900 dark:to-ink-950/40 hover:ring-brand-400 transition cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-2 font-bold text-sm truncate">
                    <span
                      className="w-3 h-3 rounded shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="truncate">{s.nameAr}</span>
                  </span>
                  <Badge tone={s.efficiency >= 100 ? 'success' : 'warn'} dot>
                    {s.efficiency}%
                  </Badge>
                </div>
                <div className="text-2xl font-extrabold tabular font-mono mt-1">
                  {fmtAR(s.produced)}
                </div>
                <div className="text-[11px] text-ink-400 mb-2">
                  قطعة · الهدف {fmtAR(s.target)}
                </div>
                <Progress
                  value={Math.min(120, s.efficiency)}
                  tone={s.efficiency >= 100 ? 'success' : 'warn'}
                />
                <div className="mt-3 pt-2 border-t border-ink-100 dark:border-ink-800 grid grid-cols-2 gap-2 text-[10px]">
                  <div>
                    <div className="text-ink-400">العمال</div>
                    <div className="font-bold tabular">{fmtAR(s.workerCount)}</div>
                  </div>
                  {stageCost && (
                    <div>
                      <div className="text-ink-400">تكلفة الشهر</div>
                      <div className="font-bold tabular text-brand-600">
                        {fmtAR(stageCost.monthlyCost)}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Operating cost summary */}
      {cost && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
                تكلفة التشغيل
              </div>
              <h3 className="font-bold text-base mt-0.5">إجمالي تكلفة العمال</h3>
            </div>
          </div>

          {/* Daily + monthly total */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-brand-50 to-white dark:from-brand-950/30 dark:to-ink-900 ring-1 ring-brand-200 dark:ring-brand-800">
              <div className="text-[11px] text-brand-700 dark:text-brand-300 font-bold uppercase mb-1">
                التكلفة اليومية
              </div>
              <div className="text-3xl font-black tabular text-brand-700 dark:text-brand-300">
                {fmtMoney(Math.round(cost.totals.dept.daily))}
              </div>
              <div className="text-[11px] text-ink-400 mt-1">
                إجمالي يومى (الشهرى ÷ 30)
              </div>
            </div>
            <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-ink-900 ring-1 ring-amber-200 dark:ring-amber-800">
              <div className="text-[11px] text-amber-700 dark:text-amber-300 font-bold uppercase mb-1">
                التكلفة الشهرية
              </div>
              <div className="text-3xl font-black tabular text-amber-700 dark:text-amber-300">
                {fmtMoney(cost.totals.dept.monthly)}
              </div>
              <div className="text-[11px] text-ink-400 mt-1">رواتب + بدلات جميع الموظفين النشطين</div>
            </div>
          </div>

        </Card>
      )}
    </div>
  );
}
